<template>
  <div id="app" :class="currentTheme">
    <!-- 全局Header -->
    <Header />
    
    <!-- 页面内容区域 -->
    <div class="page-container">
      <router-view v-slot="{ Component, route }">
        <transition 
          :name="getTransitionName(route)" 
          mode="out-in"
          @before-enter="onBeforeEnter"
          @enter="onEnter"
          @leave="onLeave"
          @before-leave="onBeforeLeave"
        >
          <keep-alive v-if="shouldKeepAlive(route)">
            <component 
              :is="Component" 
              :key="route.path"
              class="page-content"
              v-show="!pageLoading"
            />
          </keep-alive>
          <component 
            v-else
            :is="Component" 
            :key="route.path"
            class="page-content"
            v-show="!pageLoading"
          />
        </transition>
      </router-view>
    </div>
    
    <!-- 全局Loading遮罩 -->
    <transition name="fade">
      <div v-if="pageLoading" class="page-loading-overlay">
        <div class="loading-content">
          <el-icon class="loading-icon"><Loading /></el-icon>
          <p class="loading-text">页面加载中...</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import Header from '@/components/Header.vue'
import { Loading } from '@element-plus/icons-vue'

export default {
  name: 'App',
  components: {
    Header,
    Loading
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const pageLoading = ref(false)
    const currentTheme = computed(() => store.state.theme.currentTheme)
    
    // 路由过渡动画名称
    const getTransitionName = (route) => {
      // 根据路由路径决定过渡动画类型
      if (route.path === '/') return 'slide-right'
      if (route.path.includes('/category/') || route.path.includes('/product/')) return 'slide-left'
      return 'fade'
    }
    
    // 判断是否需要缓存组件
    const shouldKeepAlive = (route) => {
      // 对于这些页面使用缓存，避免重复加载
      const cachePages = ['/', '/categories', '/hot', '/new', '/about']
      return cachePages.includes(route.path)
    }
    
    // 过渡动画事件处理
    const onBeforeLeave = () => {
      // 在离开之前立即显示loading，防止闪现其他页面
      pageLoading.value = true
    }
    
    const onBeforeEnter = () => {
      // 组件开始进入时隐藏loading
      pageLoading.value = false
    }
    
    const onEnter = () => {
      // 确保页面完全加载后隐藏loading
      pageLoading.value = false
    }
    
    const onLeave = () => {
      // 页面完全离开时的处理
      // 这里不需要额外操作，因为在beforeLeave中已处理
    }
    
    // 监听路由变化
    let loadingTimer = null
    
    router.beforeEach((to, from, next) => {
      // 清除之前的定时器
      if (loadingTimer) {
        clearTimeout(loadingTimer)
      }
      
      // 只有在路由真正变化时才显示loading
      if (to.path !== from.path && !shouldKeepAlive({ path: to.path })) {
        pageLoading.value = true
        
        // 设置最大loading时间，防止一直显示
        loadingTimer = setTimeout(() => {
          pageLoading.value = false
        }, 1500)
      }
      
      next()
    })
    
    router.afterEach((to) => {
      // 路由跳转完成后的处理
      if (shouldKeepAlive({ path: to.path })) {
        // 对于缓存页面，立即隐藏loading
        pageLoading.value = false
      } else {
        // 对于非缓存页面，稍微延迟隐藏loading
        loadingTimer = setTimeout(() => {
          pageLoading.value = false
        }, 150)
      }
    })
    
    onMounted(() => {
      // 初始化时确保页面状态正确
      pageLoading.value = false
    })
    
    return {
      currentTheme,
      pageLoading,
      getTransitionName,
      shouldKeepAlive,
      onBeforeLeave,
      onBeforeEnter,
      onEnter,
      onLeave
    }
  }
}
</script>

<style lang="scss">
#app {
  min-height: 100vh;
  transition: all 0.3s ease;
}

// 亮色主题
.light-theme {
  --bg-color: #ffffff;
  --text-color: #2c3e50;
  --primary-color: #409eff;
  --secondary-color: #67c23a;
  --border-color: #dcdfe6;
  --card-bg: #ffffff;
  --header-bg: #ffffff;
  --shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

// 暗色主题
.dark-theme {
  --bg-color: #1a1a1a;
  --text-color: #e4e7ed;
  --primary-color: #409eff;
  --secondary-color: #67c23a;
  --border-color: #4c4d4f;
  --card-bg: #2d2d2d;
  --header-bg: #2d2d2d;
  --shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.3);
}

// 蓝色科技主题
.tech-blue-theme {
  --bg-color: #0f1419;
  --text-color: #c9d1d9;
  --primary-color: #58a6ff;
  --secondary-color: #7c3aed;
  --border-color: #30363d;
  --card-bg: #161b22;
  --header-bg: #161b22;
  --shadow: 0 2px 12px 0 rgba(88, 166, 255, 0.2);
}

body {
  margin: 0;
  padding: 0;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  background-color: var(--bg-color);
  color: var(--text-color);
}

* {
  box-sizing: border-box;
}

// 全局滚动条样式
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--bg-color);
}

::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--primary-color);
}

// 页面容器
.page-container {
  position: relative;
  min-height: calc(100vh - 60px); // 减去Header高度
  overflow: hidden;
  background-color: var(--bg-color);
}

.page-content {
  width: 100%;
  min-height: calc(100vh - 60px);
  background-color: var(--bg-color);
  
  // 确保在过渡期间内容不会闪现
  &[style*="display: none"] {
    visibility: hidden !important;
  }
}

// 全局Loading遮罩
.page-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  
  .loading-content {
    background: var(--card-bg);
    padding: 30px;
    border-radius: 12px;
    box-shadow: var(--shadow);
    text-align: center;
    
    .loading-icon {
      font-size: 32px;
      color: var(--primary-color);
      animation: spin 1s linear infinite;
    }
    
    .loading-text {
      margin: 12px 0 0 0;
      color: var(--text-color);
      font-size: 14px;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 淡入淡出过渡
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease-out;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active {
  z-index: 2;
}

.fade-leave-active {
  z-index: 1;
}

// 从右滑入过渡
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.slide-right-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-right-enter-active {
  z-index: 2;
}

.slide-right-leave-active {
  z-index: 1;
}

// 从左滑入过渡
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.slide-left-enter-from {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.slide-left-enter-active {
  z-index: 2;
}

.slide-left-leave-active {
  z-index: 1;
}
</style>
